import { CodePanel, type Chunk } from "@/components/guide/code-panel";
import type { Chapter } from "@/components/guide/tutorial";

/**
 * Chapter 3: the interface -- the reader's own agent, grown one method at a time, from the
 * skeleton to a run that connects, drives and checks in. The file is the kit's
 * `source/my_agent.py` with the types and the boilerplate left off; each step adds or replaces
 * a chunk, and the panel marks what changed. `arrived` is the reader's: the comment says so,
 * and the next chapter is about how to write it.
 */

const FILE: Chunk[] = [
  { from: 0, code: "class MyAgent(Agent):" },
  { from: 0, until: 1, code: "    def __init__(self, data_dir):\n        self.data_dir = data_dir" },
  {
    from: 1,
    code: "    def __init__(self, data_dir):\n        # before anything touches the server; a crash here spends nothing\n        self.frames, self.trajectories = load_frames(data_dir)\n        # build your index from them here",
  },
  { from: 0, code: "" },
  { from: 0, until: 2, code: "    def setup(self, info):\n        self.targets = info.targets" },
  {
    from: 2,
    code: "    def setup(self, info):\n        # once per session; the clock is running, so keep it cheap\n        self.targets = info.targets\n        self.goal = info.targets[0]  # view 0: arrive facing this way",
  },
  { from: 0, code: "" },
  { from: 0, until: 3, code: "    def act(self, obs):\n        raise NotImplementedError" },
  { from: 3, code: "    def act(self, obs):\n        # every step: one frame in, one move out" },
  { from: 4, code: "        if self.arrived(obs.image):  # yours: does this frame match the goal?\n            return Action.CHECKIN" },
  { from: 3, until: 5, code: "        return Action.FORWARD" },
  { from: 5, code: "        return Action.FORWARD, 4  # hold it four ticks: one round trip" },
];

const RUN: Chunk[] = [{ from: 0, code: "uv run source/my_agent.py" }];

export const agentChapter: Chapter = {
  id: "agent",
  label: "Your agent",
  title: "Implement your own agent",
  intro: "Your code talks to the simulator through three methods. Fill in the kit's skeleton one at a time and run it. You get a scored attempt driven by code before writing any perception.",
  steps: [
    {
      title: "Open the skeleton",
      body: (
        <>
          An agent is a class with three methods: <code className="font-mono text-xs">__init__</code>{" "}
          before anything connects, <code className="font-mono text-xs">setup</code> when a session
          opens, <code className="font-mono text-xs">act</code> on every step.{" "}
          <code className="font-mono text-xs">source/my_agent.py</code> has all three stubbed, each
          with a comment saying when it runs and what it may return.
        </>
      ),
    },
    {
      title: "Learn the maze first",
      body: (
        <>
          The exploration data is all you know about the maze before a run: camera frames from
          earlier drives, each with the move taken after it. Build whatever you need from it in{" "}
          <code className="font-mono text-xs">__init__</code>. It runs before the session starts, so
          it can take as long as it needs, and an error there costs no attempt.
        </>
      ),
    },
    {
      title: "Keep the goal in sight",
      body: (
        <>
          <code className="font-mono text-xs">setup</code> runs once per session with the four
          goal photos, the camera intrinsics and the step budget. The session clock is already
          running, so keep it quick. View 0 is the direction the goal faces. If the rubric scores
          heading, it is measured against that view, so arrive facing that way.
        </>
      ),
    },
    {
      title: "One frame in, one move out",
      body: (
        <>
          <code className="font-mono text-xs">act</code> is the loop. It receives the current frame
          (<code className="font-mono text-xs">obs.image</code>, 320 × 240) with the steps used and
          left, and returns one move: <code className="font-mono text-xs">FORWARD</code>,{" "}
          <code className="font-mono text-xs">BACKWARD</code>, <code className="font-mono text-xs">LEFT</code>,{" "}
          <code className="font-mono text-xs">RIGHT</code>, or two combined with{" "}
          <code className="font-mono text-xs">|</code> for an arc. The simulator applies it for one tick
          and sends the next frame. Nothing else comes back.
        </>
      ),
    },
    {
      title: "Say here",
      body: (
        <>
          Return <code className="font-mono text-xs">CHECKIN</code> and the simulator scores the
          robot&apos;s position and heading at that moment. The attempt is over. The condition is yours
          to write: <code className="font-mono text-xs">arrived</code> stands for &quot;does this frame
          match the goal photos?&quot;, which is what the next chapter is about. Checking in early
          is a miss. Never checking in runs out the step budget, also unscored.
        </>
      ),
    },
    {
      title: "Spend fewer round trips",
      body: (
        <>
          Each call to <code className="font-mono text-xs">act</code> is a network round trip, and a
          run is around 1,500 ticks. Return <code className="font-mono text-xs">(Action, n)</code> to
          apply a move for <em>n</em> ticks in one request. All the ticks still count as steps, so
          the score does not change, only how long the run takes. Four to eight is a good burst.
        </>
      ),
    },
    {
      title: "Run it",
      body: "The first run downloads the exploration data. Every run calls your setup and act once with random frames first, so a crash costs no attempt, then asks before starting one. As written this agent drives forward and checks in wherever its placeholder says. It will score a miss, and you will have seen the whole loop run under your own code.",
    },
  ],
  preview: (step) =>
    step >= 6 ? (
      <CodePanel chunks={RUN} step={0} language="bash" title="Terminal" />
    ) : (
      <CodePanel chunks={FILE} step={step} language="python" title="source/my_agent.py" />
    ),
};
